import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withFeature, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { catchError, EMPTY, exhaustMap, filter, pipe } from 'rxjs';
import { Direction, DocumentType } from '../../enum';
import { RanneyWell, RanneyWellDocument } from '../../model';
import { DocumentService, RanneyWellService } from '../../services';
import { CrudSeverity, initialState, withCommonMethods, withCrudMethods } from '../../state';
import { withFlowMethods } from './state/flow.feature';
import { withDrainMethods } from './state/drain.feature';
import { withEquipmentMethods } from './state/equipment.feature';
import { withWorkMethods } from './state/work.feature';

const initState = initialState<RanneyWell>({ direction: Direction.ASC });
export const RanneyWellStore = signalStore(
  withState(initState),
  withCrudMethods(RanneyWellService),
  withCommonMethods(),
  withMethods(store => {
    const service = inject(RanneyWellService);
    const documentService = inject(DocumentService);
    const messageService = inject(MessageService);
    const translate = inject(TranslateService);
    const deleteDocument = rxMethod<RanneyWellDocument | undefined>(
      pipe(
        filter(rwDocument => undefined !== rwDocument),
        exhaustMap(rwDocument =>
          service.deleteDocument(rwDocument.document.id).pipe(
            tapResponse({
              next: () => {
                patchState(store, state => {
                  const active = state.active;
                  return {
                    active: active
                      ? {
                          ...active,
                          ranneyWellDocuments:
                            active.ranneyWellDocuments
                              ?.filter(doc => doc.document.id !== rwDocument.document.id)
                              .map(doc => ({ ...doc, document: { ...doc.document } })) ?? undefined,
                        }
                      : undefined,
                  };
                });
                messageService.add({
                  severity: CrudSeverity.success,
                  summary: translate.instant('rb.success'),
                  detail: translate.instant('rb.successDelete'),
                });
              },
              error: console.error,
            })
          )
        )
      )
    );
    const uploadDocument = rxMethod<
      | {
          rwId: number;
          workId: number;
          document: File;
        }
      | undefined
    >(
      pipe(
        filter(data => undefined !== data),
        exhaustMap(({ rwId, workId, document }) =>
          documentService.upload(document).pipe(
            catchError(() => EMPTY),
            exhaustMap(rsp =>
              service
                .addDocument(rwId, {
                  documentId: rsp.id,
                  documentType: DocumentType.RW_WORK,
                  ranneyWellWorkId: workId,
                })
                .pipe(
                  tapResponse({
                    next: rw => {
                      patchState(store, state => {
                        const active = state.active;
                        return {
                          active: active
                            ? {
                                ...active,
                                ...rw,
                              }
                            : undefined,
                        };
                      });
                      messageService.add({
                        severity: CrudSeverity.success,
                        summary: translate.instant('rb.success'),
                        detail: translate.instant('rb.successUpload'),
                      });
                    },
                    error: console.error,
                  })
                )
            )
          )
        )
      )
    );
    return { deleteDocument, uploadDocument };
  }),
  withFeature(() => withFlowMethods()),
  withFeature(() => withDrainMethods()),
  withFeature(() => withEquipmentMethods()),
  withFeature(() => withWorkMethods())
);
